import React, { useState } from "react";
import { Check, Shield, Star, BarChart, Zap, Users, Facebook, Linkedin, Instagram } from "lucide-react";

export default function WaitlistSignup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  async function onSubmit(e) {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });

    try {
      const apiBase = process.env.REACT_APP_API_URL || "";
      const res = await fetch(`${apiBase}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, marketing }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatus({
          state: "error",
          message: data?.error || "Something went wrong",
        });
        return;
      }

      window.location.href = "/waitlist-success";
    } catch {
      setStatus({ state: "error", message: "Network error" });
    }
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white flex flex-col">
      {/* NAV */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-white/10">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
          alt="Beem logo"
          className="w-24"
        />
        <div className="flex items-center gap-4 text-gray-400 text-sm">
          <div className="flex items-center gap-1"><Shield size={14}/> GDPR Ready</div>
        </div>
      </div>

      <div className="flex flex-1 flex-col md:flex-row">

        {/* LEFT */}
        <div className="w-full md:w-1/2 flex flex-col justify-center px-8 md:px-16 py-12">

          <div className="mb-4 flex items-center gap-2 text-[#AF9B8A]">
            <Users size={16}/> Software built for the modern property manager
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Finally, a platform landlords don’t secretly hate
          </h1>

          <p className="text-gray-300 text-lg mb-6 max-w-xl">
            Replace spreadsheets, clunky tools and late-night headaches with one clean system that actually works.
          </p>

          {/* OFFER */}
          <div className="bg-[#AF9B8A]/10 border border-[#AF9B8A]/30 rounded-xl p-4 mb-8">
            <p className="font-semibold text-[#AF9B8A]">
              First 500 users get 3 months free.
            </p>
            <p className="text-sm text-gray-400">
              We are offering the first 500 users an exclusive 3 months free when we launch. Join the waitlist to secure your spot and be among the first to experience the future of property management.
            </p>
          </div>

                    {/*  CTA BUTTON */}
          <button
            onClick={() =>
              document.getElementById("waitlist-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="mb-6 inline-block bg-[#AF9B8A] hover:bg-[#9c8a79] text-white font-semibold px-6 py-3 rounded-lg transition"
          >
            Get early access →
          </button>

          {/* BENEFITS */}
          <div className="space-y-3 mb-10 text-gray-300">
            <div className="flex items-center gap-3"><Check size={16}/> Automate rent collection</div>
            <div className="flex items-center gap-3"><Check size={16}/> Tenant CRM built-in</div>
            <div className="flex items-center gap-3"><Check size={16}/> Contracts & tenant onboarding</div>
            <div className="flex items-center gap-3"><Check size={16}/> Real-time analytics</div>
          </div>

          {/* FORM */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md backdrop-blur">

            <h3 className="text-xl font-semibold mb-2">Get the early accsess perks!</h3>
            <p className="text-sm text-gray-400 mb-4">
              It takes 10 seconds. Making a cup of tea takes longer...
            </p>

            <form id="waitlist-form" onSubmit={onSubmit} className="space-y-4">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              />

              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                type="email"
                placeholder="you@email.com"
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              />

              <label className="flex items-start gap-2 text-xs text-gray-400">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1"
                />
                Send me product updates and occasional marketing
              </label>

              <button
                type="submit"
                disabled={status.state === "loading"}
                className="w-full bg-[#AF9B8A] hover:bg-[#9c8a79] text-white font-semibold py-3 rounded-lg transition"
              >
                {status.state === "loading" ? "Saving..." : "Claim my perks"}
              </button>

              {status.state !== "idle" && (
                <p className={`text-center text-sm ${status.state === "error" ? "text-red-400" : "text-green-400"}`}>
                  {status.message}
                </p>
              )}
            </form>
          </div>

        </div>

        {/* RIGHT */}
        <div className="hidden md:flex w-1/2 relative items-center justify-center">

          <img
            src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
            alt="City property management illustration"
            className="absolute inset-0 w-full h-full object-cover opacity-70"
          />

          <div className="relative z-10 p-10 max-w-md">

            <div className="bg-black/60 backdrop-blur border border-white/10 p-6 rounded-2xl mb-6">
              <p className="text-lg mb-4">
                "I cannot wait for Beem to launch, it would replace 4 tools I already pay for”
              </p>
              <div className="text-sm text-gray-400">
                — James K, Property Investor
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-300">
              <div className="bg-black/50 p-4 rounded-xl border border-white/10 flex items-center gap-2"><Shield size={14}/> Secure</div>
              <div className="bg-black/50 p-4 rounded-xl border border-white/10 flex items-center gap-2"><Zap size={14}/> Fast</div>
              <div className="bg-black/50 p-4 rounded-xl border border-white/10 flex items-center gap-2"><BarChart size={14}/> Insights</div>
              <div className="bg-black/50 p-4 rounded-xl border border-white/10 flex items-center gap-2"><Star size={14}/> Simple dashboard</div>
            </div>
          </div>

          <div className="absolute inset-0 bg-gradient-to-l from-black via-black/60 to-transparent" />
        </div>
      </div>

      {/* FOOTER */}
      <div className="flex flex-col md:flex-row items-center justify-between px-6 py-6 border-t border-white/10 text-gray-400 text-sm">
        <div>© {new Date().getFullYear()} Beem</div>

        <div className="flex gap-4 mt-4 md:mt-0">
          <a href="https://www.facebook.com/beem.tech" target="_blank" rel="noopener noreferrer">
            <Facebook size={18} className="hover:text-white transition" />
          </a>
          <a href="https://www.linkedin.com/company/beem-tech/">
            <Linkedin size={18} className="hover:text-white transition" />
          </a>
          <a href="https://www.instagram.com/beem.properties/">
            <Instagram size={18} className="hover:text-white transition" />
          </a>
        </div>
      </div>
    </div>
  );
}
